import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { clearSessionStorage, getNewToken, postRequest } from '../services/httpservice';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableFooter from '@mui/material/TableFooter';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import xpay from "../assets/xpay.png";
import FirstPageIcon from '@mui/icons-material/FirstPage';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import LastPageIcon from '@mui/icons-material/LastPage';
import Button from '@mui/material/Button'; // Import Button component from MUI
import { useNavigate } from 'react-router-dom';
import { apiURL } from '../constants/apiUrlConstants';
import { useDispatch, useSelector } from 'react-redux';
import { setCivilIdAction } from '../Redux/actions';
import { RootState } from '../Redux/reducer';
import KuwaitiDinarFormat from './KuwaitiDinarFormat';
import { setLoanAccountNumbers } from '../Redux/loanAccountNumbersSlice';
import Loader from './Loader';
import axios, { AxiosError } from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Typography } from '@mui/material';
interface DealsTableProps {
    civilId: string | null;
    onQuickPayAmountChange: (amount: number) => void;
    onCustomerNumberChange: (customerNumber: string) => void;  
    // onNameUpdate: (name: string, nameArabic: string) => void;
    // onEmailUpdate: (email: string | null) => void;
}

interface TablePaginationActionsProps {
    count: number;
    page: number;
    rowsPerPage: number;
    onPageChange: (
        event: React.MouseEvent<HTMLButtonElement>,
        newPage: number,
    ) => void;
}

interface Deals {
    type: string;
    loanAccountNo: string;
    invoiceNumber: string;
    pendingInstallments: string;
    installmentDue: string;
    outstandingAmount: string;
    lateInstallment: string | null;
    lateInstallmentAmount: string | null;
    customerNumber: string;
    imgUrl: string;

}

interface ApiResponse {
    civilId: number;
    customerNumber: string;
    name: string;
    email: string | null;
    nameArabic: string;
    status: string;
    message: string;
    dealDetails: Deals[];
}

function TablePaginationActions(props: TablePaginationActionsProps) {
    const theme = useTheme();
    const { count, page, rowsPerPage, onPageChange } = props;

    const handleFirstPageButtonClick = (
        event: React.MouseEvent<HTMLButtonElement>,
    ) => {
        onPageChange(event, 0);
    };

    const handleBackButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        onPageChange(event, page - 1);
    };

    const handleNextButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        onPageChange(event, page + 1);
    };

    const handleLastPageButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
    };

    return (
        <Box sx={{ flexShrink: 0, ml: 2.5 }}>
            {/* <IconButton
                onClick={handleFirstPageButtonClick}
                disabled={page === 0}
                aria-label="first page"
            >
                {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
            </IconButton> */}
            <IconButton
                onClick={handleBackButtonClick}
                disabled={page === 0}
                aria-label="previous page"
            >
                {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
            </IconButton>
            <IconButton
                onClick={handleNextButtonClick}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                aria-label="next page"
            >
                {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
            </IconButton>
            {/* <IconButton
                onClick={handleLastPageButtonClick}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                aria-label="last page"
            >
                {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
            </IconButton> */}
        </Box>
    );
}
const DealsTable: React.FC<DealsTableProps> = ({ civilId, onQuickPayAmountChange, onCustomerNumberChange }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);
    const [deals, setDeals] = useState<Deals[]>([]);
    const [dealData, setDealData] = useState<ApiResponse | null>(null);
    const [quickPayAmount, setQuickPayAmount] = useState(0);
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const loanAccountNumbers = useSelector((state: RootState) => state.loanAccountNumbers);
    const [toastCloseInMs, setToastCloseInMs] = useState<number>(0);
    useEffect(() => {
        if (civilId) {
            const msToast = process.env.REACT_APP_TOAST_CLOSE_IN_SECONDS;
            if(msToast) {
            setToastCloseInMs(parseInt(msToast));
            }
            getDealDetails();
        }
        // else {
        //     navigate('/');
        // }
    }, [civilId]);

    useEffect(() => {
        // Calculate the sum of lateInstallmentAmount
        const sum = deals.reduce((acc, row) => acc + Number(row.lateInstallmentAmount), 0);
        setQuickPayAmount(sum);
        onQuickPayAmountChange(sum);
    }, [civilId, deals]);
   
    const getDealDetails = async () => {
        setIsLoading(true);
        try {
            const accessToken = sessionStorage.getItem('accessToken');
            if (!accessToken) {
                navigate('/');
                return;
            }
            const response = await postRequest(
                process.env.REACT_APP_API_URL + apiURL.GET_DEALS, {
                civilId: civilId,
            }
            );
            setIsLoading(false);
            if (response == null) {
                toast.dismiss();
                toast.error(process.env.REACT_APP_ERROR_MSG);
                return;
            }
            if (response.status == 401) {
                toast.dismiss();
                toast.error('Session expired');
                clearSessionStorage();
                setTimeout(() => {
                    navigate('/');
                  }, 2000);
                return;
            }
            if(response.data=== '') {
                toast.dismiss();
                toast.error(process.env.REACT_APP_ERROR_MSG);
                return;
            }
            
            const data = response.data;
            setDealData(data);
            setDeals(data.dealDetails || []);

            const filteredDeals = (data.dealDetails || []).filter((deal: Deals) => Number(deal.lateInstallmentAmount) > 0);
            const newLoanAccountObjects = filteredDeals.map((deal: Deals) => ({
                loanAccountNo: deal.loanAccountNo,
                type: deal.type // replace with the actual property name for the type
            }));
            dispatch(setLoanAccountNumbers(newLoanAccountObjects));
            onCustomerNumberChange(data.customerNumber);
            setIsLoading(false);
            getNewToken();
        } catch (error:any) {
            if (axios.isAxiosError(error)) {
                toast.dismiss();
                const axiosError = error as AxiosError;
                if (axiosError.response) {
                    if (axiosError?.response.status == 401) {
                        toast.error('Session expired');
                        clearSessionStorage();
                        setTimeout(() => {
                            navigate('/');
                          }, 2000);
                    }else {
                        toast.error(process.env.REACT_APP_ERROR_MSG);
                    }
                }
            }
        }
    }

    const handleNavigate = (loanAccountNo: string, type: string) => {
        navigate('/home/installments', { state: { loanAccountNo, type } });
    };
    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - deals?.length) : 0;

    const handleChangePage = (
        event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number,
    ) => {
        setPage(newPage);
    };
    
    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };
    function getImgsrc(imgPath:string){
        try {
            return require('../'+imgPath);
        } catch (error) {
            return require('../assets/BUImages/bu-Xcite.png');    
        }
      }
    return (
        <>
            {isLoading && <Loader />}
            <TableContainer className='centerAlign-Table' component={Paper} sx={{ width: "100%"  ,   pt: 0,}}>
                <Table sx={{ minWidth: { md: 500 } }} size="small" aria-label="Deals Table">
                    <TableHead>
                        <TableRow className='tableRow'  sx={{
    "& .MuiTableCell-root": {
      width: "127px",
      height: "106px",
      opacity: 1,
      borderRight: "1px solid #E0E0E0",
      borderLeft: "1px solid #E0E0E0",
      pt: "30px",
      pb: "30px",
      color:"rgba(0, 16, 75, 0.87)",
      fontWeight: 600,
      fontSize: "13px",
      lineHeight: "163%",
      letterSpacing: "0%",
      textAlign: "center",
      verticalAlign: "middle",
    },
  }}>
                        <TableCell>{t('dealsTable.type')}</TableCell>
              <TableCell>{t('dealsTable.accountNumber').split('\n').map((l: string, i: number) => i === 0 ? l : [<br key={i}/>,l])}</TableCell>
              <TableCell>{t('dealsTable.invoiceNumber').split('\n').map((l: string, i: number) => i === 0 ? l : [<br key={i}/>,l])}</TableCell>
              <TableCell>{t('dealsTable.pendingInstallments').split('\n').map((l: string, i: number) => i === 0 ? l : [<br key={i}/>,l])}</TableCell>
              <TableCell>{t('dealsTable.installmentsValue').split('\n').map((l: string, i: number) => i === 0 ? l : [<br key={i}/>,l])}</TableCell>
              <TableCell>{t('dealsTable.outstandingAmount').split('\n').map((l: string, i: number) => i === 0 ? l : [<br key={i}/>,l])}</TableCell>
              <TableCell>{t('dealsTable.lateInstallment').split('\n').map((l: string, i: number) => i === 0 ? l : [<br key={i}/>,l])}</TableCell>
              <TableCell>{t('dealsTable.lateInstallmentAmounts').split('\n').map((l: string, i: number) => i === 0 ? l : [<br key={i}/>,l])}</TableCell>
                            <TableCell className='freeze' sx={{
                                position: { xs: 'sticky', md: 'static' },
                                right: { xs: 0, md: 'auto' },
                                backgroundColor: { xs: '#F8F8F8', sm: '#FFF' },
                                width: { xs: '20px', md: 'auto' },
                            }} >{t('dealsTable.pay')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody className='transactionTable-tablebody' sx={{
    "& .MuiTableRow-root": {
      height: "54px",
    },
    "& .MuiTableCell-root": {
      height: "54px",
      padding: "0 16px",
      verticalAlign: "middle",
      boxSizing: "border-box",
    },
  }} >
                             {deals.length === 0 && !isLoading && (
                                          <TableRow>
                                            <TableCell colSpan={9} align="center" sx={{ py: 4, color: '#888', fontSize: '15px' }}>
                                              {t('deals.noDataFound', 'No data found')}
                                            </TableCell>
                                          </TableRow>
                                        )}
                        {(rowsPerPage > 0
                            ? deals?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            : deals
                        )?.map((row, index) => (
                            <TableRow className='tableRow' key={index}>
                                <TableCell scope="row">
                                   {
                                     <img className='deals-imgStyle'
                                     src={getImgsrc(row.imgUrl)}
                                     alt="Logo"
                                     style={{ verticalAlign: 'middle' }} />
                                   }
                                </TableCell>
                                <TableCell >{row.loanAccountNo}</TableCell>
                                <TableCell >{row.invoiceNumber}</TableCell>
                                <TableCell >{row.pendingInstallments}</TableCell>
                                <TableCell >
                                    <KuwaitiDinarFormat amount={Number(row.installmentDue)} />
                                </TableCell>
                                <TableCell >
                                    <KuwaitiDinarFormat amount={Number(row.outstandingAmount)} />
                                </TableCell>
                                <TableCell >{row.lateInstallment}</TableCell>
                                <TableCell >
                                    <KuwaitiDinarFormat amount={Number(row.lateInstallmentAmount)} />
                                </TableCell>
                                <TableCell className='freeze' sx={{
                                    position: { xs: 'sticky', md: 'static' },
                                    right: { xs: 0, md: 'auto' },
                                    backgroundColor: { xs: '#F8F8F8', sm: '#FFF' },
                                    width: { xs: '21px', md: 'auto' }
                                }} >
                                 
                                    <Button   sx={{
    backgroundColor: '#2A92BD'}} variant="contained" size="medium" className='deals-linkStyle' onClick={() => handleNavigate(row.loanAccountNo, row.type)}>
                                        {t('dealsTable.pay')} 
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                        {emptyRows > 0 && (
                            <TableRow className='.transactionTable-emptyRow' >
                                <TableCell colSpan={10} />
                            </TableRow>
                        )}
                    </TableBody>
                    {/* <TableBody className="transactionTable-tablebody">
  <TableRow className="tableRow">
    <TableCell>
       <img src={xpay} alt="Logo" style={{ width: '88px' }} />
    </TableCell>

    <TableCell>LN100245</TableCell>
    <TableCell>INV-1001</TableCell>
    <TableCell>3</TableCell>

    <TableCell>KWD 120.500</TableCell>

    <TableCell>KWD 850.750</TableCell>

    <TableCell>1</TableCell>

    <TableCell>KWD 40.250</TableCell>

    <TableCell
      className="freeze"
      sx={{
        position: { xs: "sticky", md: "static" },
        right: { xs: 0, md: "auto" },
        
        backgroundColor: { xs: "#F8F8F8", sm: "#FFF" },
        width: { xs: "21px", md: "auto" },
      }}
    >
      <Button 
        variant="contained"
        size="medium"
        className="deals-linkStyle"
         onClick={() => navigate("/home/installments")}
          sx={{
    backgroundColor: "#2A92BD",
    "&:hover": {
      backgroundColor: "#2A92BD",
    },
  }}
      >
         {t('dealsTable.pay')}
      </Button>
    </TableCell>
  </TableRow>
  <TableRow className="tableRow">
    <TableCell>
       <img src={xpay} alt="Logo" style={{ width: '88px' }} />
    </TableCell>

    <TableCell>LN100245</TableCell>
    <TableCell>INV-1001</TableCell>
    <TableCell>3</TableCell>

    <TableCell>KWD 120.500</TableCell>

    <TableCell>KWD 850.750</TableCell>

    <TableCell>1</TableCell>

    <TableCell>KWD 40.250</TableCell>

    <TableCell
      className="freeze"
      sx={{
        position: { xs: "sticky", md: "static" },
        right: { xs: 0, md: "auto" },
        
        backgroundColor: { xs: "#F8F8F8", sm: "#FFF" },
        width: { xs: "21px", md: "auto" },
      }}
    >
      <Button 
        variant="contained"
        size="medium"
        className="deals-linkStyle"
         onClick={() => navigate("/home/installments")}
          sx={{
    backgroundColor: "#2A92BD",
    "&:hover": {
      backgroundColor: "#2A92BD",
    },
  }}
      >
         {t('dealsTable.pay')}
      </Button>
    </TableCell>
  </TableRow>
  <TableRow className="tableRow">
    <TableCell>
       <img src={xpay} alt="Logo" style={{ width: '88px' }} />
    </TableCell>

    <TableCell>LN100245</TableCell>
    <TableCell>INV-1001</TableCell>
    <TableCell>3</TableCell>

    <TableCell>KWD 120.500</TableCell>

    <TableCell>KWD 850.750</TableCell>

    <TableCell>1</TableCell>

    <TableCell>KWD 40.250</TableCell>

    <TableCell
      className="freeze"
      sx={{
        position: { xs: "sticky", md: "static" },
        right: { xs: 0, md: "auto" },
        
        backgroundColor: { xs: "#F8F8F8", sm: "#FFF" },
        width: { xs: "21px", md: "auto" },
      }}
    >
      <Button 
        variant="contained"
        size="medium"
        className="deals-linkStyle"
         onClick={() => navigate("/home/installments")}
          sx={{
    backgroundColor: "#2A92BD",
    "&:hover": {
      backgroundColor: "#2A92BD",
    },
  }}
      >
         {t('dealsTable.pay')}
      </Button>
    </TableCell>
  </TableRow>
</TableBody>
                   */}
                    
                </Table>
          
            </TableContainer>
            
            <ToastContainer
          position="top-right"
          newestOnTop
          limit={1}
          autoClose={toastCloseInMs}
        />
<Box
  className="transactionTable-footer"
  sx={{
    width: "100%",
    display: "flex",
    justifyContent: "flex-end",
    textAlign:"center",
    mt: 1,
  }}
>
  <TablePagination
    component="div"
    count={deals?.length}
    rowsPerPage={rowsPerPage}
    page={page}
    onPageChange={handleChangePage}
    onRowsPerPageChange={handleChangeRowsPerPage}
    ActionsComponent={TablePaginationActions}
    labelRowsPerPage=""
    rowsPerPageOptions={[]}
  />
</Box>
        {/* <Typography className='quickPayTypo' sx={{ mt: 1 ,    textAlign: "right",}}> 
  {t('dealsTable.emailNote')}
</Typography> */}
        </>
    );
}
export default DealsTable;
